import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { PageBackground } from '@/components/ui/page-background';
import { PageHero } from '@/components/ui/page-hero';
import { ArrowRight, Check, Monitor, Zap, Settings, Bot, ChevronDown } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

const pricingPlans = [
  {
    name: "Website Essentials",
    price: "$500",
    period: "/year",
    icon: Monitor,
    bestFor: "Small businesses that want a professional website without the headache",
    description: "Everything you need for a professional web presence",
    features: [
      "Hosting + SSL + uptime monitoring",
      "Up to 6 pages (Home, About, Services, Contact, +2 custom)",
      "Responsive design, GA4, on-page SEO basics, cookie banner",
      "Content updates up to 1h/month"
    ],
    cta: "Choose Essentials",
    popular: false
  },
  {
    name: "Growth Website + SEO",
    price: "$800", 
    period: "/year",
    icon: Zap,
    bestFor: "Teams who need monthly growth and measurable improvements",
    description: "Everything in Essentials plus monthly SEO optimization",
    features: [
      "Everything in Essentials",
      "Monthly SEO tasks (technical fixes, content tweaks, internal links)",
      "1 landing page/quarter",
      "Core Web Vitals checks",
      "2h updates/month"
    ],
    cta: "Choose Growth + SEO",
    popular: true
  },
  {
    name: "AI Chatbot",
    price: "$500",
    period: "/year",
    icon: Bot,
    bestFor: "Existing websites that need intelligent customer support",
    description: "Standalone chatbot for your website",
    features: [
      "Chatbot setup for FAQs + lead capture",
      "Trained on your content (docs, FAQs, services)",
      "Routing to email + conversation transcripts",
      "Conversational SEO scripts for users' intent"
    ],
    cta: "Choose AI Chatbot",
    popular: false
  },
  {
    name: "Website + SEO + Chatbot",
    price: "$1,000",
    period: "/year",
    icon: Settings,
    bestFor: "Complete solution with website, SEO, and smart chatbot",
    description: "Website Essentials + monthly SEO + AI Chatbot included",
    features: [
      "Website Essentials + monthly SEO + AI Chatbot included",
      "Same details as Growth + SEO plus the chatbot features above"
    ],
    cta: "Choose Bundle",
    popular: false
  },
  {
    name: "Custom App / Web App / MVP",
    price: "Custom",
    period: "",
    icon: Settings,
    bestFor: "SaaS founders and teams who need apps that solve real business problems",
    description: "Full-scale application development with proven process",
    features: [
      "Web apps, mobile apps, automations/integrations",
      "Process: Strategy → UX/UI sprints → Build & weekly demos → Test & launch"
    ],
    cta: "Request Quote",
    popular: false
  }
];

const faqs = [
  {
    question: "What's included in each plan?",
    answer: "Each plan includes everything listed in its features. Website plans include hosting, SSL, and ongoing support. Custom app projects include full development, design, and launch support."
  },
  {
    question: "Can I add more pages or features?",
    answer: "Yes! Additional pages and features can be added to any website plan. We'll provide a clear quote for any add-ons before starting work."
  },
  {
    question: "How do updates and content changes work?",
    answer: "Website Essentials includes 1 hour of content updates per month. Growth + SEO includes 2 hours. Larger changes are scoped separately with transparent pricing."
  },
  {
    question: "What about domain and email hosting?",
    answer: "All website plans include web hosting and SSL. Domain registration and email hosting are additional services we can help you set up."
  },
  {
    question: "Can I switch between plans?",
    answer: "Absolutely! You can upgrade or downgrade at any time. We'll pro-rate the difference and adjust your billing accordingly."
  },
  {
    question: "How is onboarding handled?",
    answer: "After choosing a plan, we'll schedule a discovery call to understand your needs, then provide a detailed project timeline and next steps within 24 hours."
  }
];

const Pricing = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  return (
    <PageBackground>
      <SEO
        title="Pricing - Web Development & Design Packages | AffinityAim"
        description="Transparent web development pricing. Starter, Growth, and Enterprise packages. Custom quotes for AI integration and complex projects."
        canonical="https://www.affinityaim.com/pricing"
        keywords="website pricing, annual website plans, chatbot pricing, web app development cost, custom app pricing"
        schema={{
          "@context": "https://schema.org",
          "@type": "OfferCatalog",
          "name": "AffinityAim Pricing"
        }}
      />
      <Navigation />

      <main id="main-content">
        <PageHero
          label="Pricing"
          title="Simple, transparent pricing"
          titleAccent="for every business"
          description="5 annual plans for websites, chatbots, and custom apps. No hidden fees, no surprises."
        />

        {/* Pricing Models */}
        <section className="pb-24">
          <div className="container mx-auto px-6">
            <div ref={elementRef} className="max-w-7xl mx-auto">
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                {pricingPlans.map((plan, index) => {
                  const IconComponent = plan.icon;
                  return (
                    <div 
                      key={index}
                      className={`relative p-6 rounded-2xl transition-all duration-500 hover:shadow-lg ${
                        plan.popular 
                          ? 'bg-gradient-to-b from-primary/10 to-surface/50 border-2 border-primary/40 hover:shadow-primary/20' 
                          : 'bg-surface/50 border border-border hover:border-primary/30 hover:shadow-primary/5'
                      }`}
                      style={getRevealStyle(index)}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-semibold shadow-lg shadow-primary/30">
                            Most Popular
                          </div>
                        </div>
                      )}
                      
                      {/* Header */}
                      <div className="mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center mb-4">
                          <IconComponent className="w-5 h-5 text-primary" />
                        </div>
                        
                        <h3 className="font-display font-semibold text-foreground text-lg mb-3">
                          {plan.name}
                        </h3>
                        
                        <div className="flex items-baseline gap-1 mb-3">
                          <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                          <span className="text-muted-foreground text-sm">{plan.period}</span>
                        </div>
                        
                        <p className="text-xs text-primary font-medium mb-3 leading-relaxed">
                          {plan.bestFor}
                        </p>
                        
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {plan.description}
                        </p>
                      </div>

                      {/* Features */}
                      <div className="mb-6">
                        <div className="space-y-3">
                          {plan.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <div className="w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-primary" />
                              </div>
                              <span className="text-sm text-muted-foreground leading-relaxed">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CTA */}
                      <Link to={`/contact?plan=${encodeURIComponent(plan.name)}`}>
                        <Button 
                          variant={plan.popular ? "default" : "outline"} 
                          className={`w-full group ${
                            plan.popular 
                              ? 'bg-primary hover:bg-primary/90' 
                              : 'border-border hover:border-primary/30 hover:bg-surface'
                          }`}
                        >
                          {plan.cta}
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>

                    </div>
                  );
                })}
              </div>
              
              <div className="text-center mt-10">
                <p className="text-sm text-muted-foreground">
                  Exact scope and any add-ons are confirmed after a short call.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-24 bg-surface/30">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              
              <div className="text-center mb-16">
                <div className="inline-flex items-center px-3 py-1 rounded-full border border-border bg-surface/50 mb-6">
                  <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                    FAQ
                  </span>
                </div>
                <h2 className="font-display font-bold text-foreground mb-4" style={{ fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: '1.2' }}>
                  Common questions
                </h2>
                <p className="text-muted-foreground">
                  Everything you need to know about working with us.
                </p>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className={`rounded-xl border transition-all duration-300 ${
                      expandedFAQ === index 
                        ? 'bg-surface/80 border-primary/30' 
                        : 'bg-surface/50 border-border hover:border-primary/20'
                    }`}
                  >
                    <button
                      className="w-full p-5 text-left flex items-center justify-between gap-4 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-xl tap-target"
                      onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                      aria-expanded={expandedFAQ === index}
                    >
                      <span className="font-medium text-foreground">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                          expandedFAQ === index ? 'rotate-180' : ''
                        }`} 
                      />
                    </button>
                    
                    {expandedFAQ === index && (
                      <div className="px-5 pb-5">
                        <div className="h-px bg-border mb-4" />
                        <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
          
          <div className="container mx-auto px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              
              <h2 className="font-display font-bold text-foreground mb-6" style={{ fontSize: 'clamp(32px, 5vw, 48px)', lineHeight: '1.1' }}>
                Ready to get started?
              </h2>
              <p className="text-muted-foreground mb-10 text-lg max-w-xl mx-auto">
                Book a 15-minute call to discuss your project and get a custom estimate.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-14 text-base">
                    Book strategy call
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="outline" size="lg" className="h-14 px-8 text-base border-border hover:bg-surface hover:border-primary/30">
                    View our work
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </PageBackground>
  );
};

export default Pricing;
